import React, { useState, useEffect } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import axios, { FILE_URL } from '../../api/axios';
import AdminLayout from '../../layouts/AdminLayout';
import { useCart } from '../../context/CartContext';
import { toast } from 'react-toastify';
import { FaEye, FaEyeSlash, FaShoppingCart, FaArrowLeft, FaCheckCircle, FaBoxOpen, FaStar, FaTruck, FaShieldAlt, FaHeart, FaRegHeart, FaTimes, FaChevronLeft, FaChevronRight } from 'react-icons/fa';
import DOMPurify from 'dompurify';

const ProductDetail = () => {
    const { id } = useParams();
    const { i18n } = useTranslation();
    const navigate = useNavigate();
    const [product, setProduct] = useState(null);
    const [selectedVariation, setSelectedVariation] = useState(null);
    const [quantity, setQuantity] = useState(1);
    const [showDealerPrice, setShowDealerPrice] = useState(false); // Varsayılan olarak gizli
    const [isFavorite, setIsFavorite] = useState(false);
    const [mainImage, setMainImage] = useState('');
    const [isLightboxOpen, setIsLightboxOpen] = useState(false); // Lightbox durumu
    const { addToCart } = useCart();

    useEffect(() => {
        const fetchProduct = async () => {
            try {
                const res = await axios.get(`/products/${id}`);
                setProduct(res.data);
                if (res.data.variations && res.data.variations.length > 0) {
                    setSelectedVariation(res.data.variations[0]);
                }
                if (res.data.images && res.data.images.length > 0) {
                    const main = res.data.images.find(img => img.is_main) || res.data.images[0];
                    setMainImage(main.image_url);
                }
                setQuantity(res.data.min_order_limit || 1);
                window.scrollTo(0, 0);
            } catch (error) {
                console.error('Ürün detay hatası', error);
            }
        };
        fetchProduct();
    }, [id]);

    // Favori Durumunu Kontrol Et
    useEffect(() => {
        const checkFavorite = async () => {
            try {
                const res = await axios.get(`/favorites/check/${id}`);
                setIsFavorite(res.data.isFavorite);
            } catch (error) {
                console.error('Favori durumu alınamadı');
            }
        };
        if (id) checkFavorite();
    }, [id]);

    const handleToggleFavorite = async () => {
        try {
            const res = await axios.post('/favorites/toggle', { productId: id });
            setIsFavorite(res.data.isFavorite);
            toast.success(res.data.message);
        } catch (error) {
            toast.error('İşlem başarısız.');
        }
    };

    const handleAddToCart = () => {
        if (!selectedVariation) {
            toast.error('Varyasyon seçiniz.');
            return;
        }
        if (selectedVariation.stock_quantity < quantity) {
            toast.error(`Yetersiz stok! En fazla ${selectedVariation.stock_quantity} adet alabilirsiniz.`);
            return;
        }
        addToCart(product, selectedVariation, quantity, selectedVariation.retail_price);
        toast.success('Ürün sepete eklendi!');
    };

    // Resim Kaydırma Fonksiyonları
    const handleNextImage = (e) => {
        e.stopPropagation();
        if (!product.images || product.images.length <= 1) return;
        const currentIndex = product.images.findIndex(img => img.image_url === mainImage);
        const nextIndex = (currentIndex + 1) % product.images.length;
        setMainImage(product.images[nextIndex].image_url);
    };

    const handlePrevImage = (e) => {
        e.stopPropagation();
        if (!product.images || product.images.length <= 1) return;
        const currentIndex = product.images.findIndex(img => img.image_url === mainImage);
        const prevIndex = (currentIndex - 1 + product.images.length) % product.images.length;
        setMainImage(product.images[prevIndex].image_url);
    };

    if (!product) return <AdminLayout><div className="p-5 text-center">Yükleniyor...</div></AdminLayout>;

    return (
        <AdminLayout>
            <div className="container-fluid py-4" style={{ maxWidth: '100%', overflowX: 'hidden' }}>
                <div className="d-flex align-items-center mb-4">
                    <button onClick={() => navigate(-1)} className="btn btn-outline-secondary btn-sm me-3 rounded-pill px-3 d-flex align-items-center">
                        <FaArrowLeft className="me-2" /> Geri
                    </button>
                    {/* Breadcrumb */}
                    <nav aria-label="breadcrumb" className="mb-0">
                        <ol className="breadcrumb breadcrumb-custom mb-0">
                            <li className="breadcrumb-item"><Link to="/dashboard">Ana Sayfa</Link></li>
                            <li className="breadcrumb-item"><Link to="/products">Ürünler</Link></li>
                            <li className="breadcrumb-item active" aria-current="page">{product.name}</li>
                        </ol>
                    </nav>
                </div>

                <div className="card border-0 shadow-lg rounded-4 overflow-hidden mb-5">
                    <div className="row g-0">
                        {/* Sol Taraf: Resim Galerisi */}
                        <div className="col-lg-6 bg-light position-relative">
                            <div className="h-100 d-flex flex-column align-items-center justify-content-center p-5">
                                <div 
                                    className="main-image-wrapper bg-white p-3 rounded-4 shadow-sm mb-4 position-relative" 
                                    style={{ width: '100%', maxWidth: '500px', aspectRatio: '1/1', display: 'flex', alignItems: 'center', justifyContent: 'center' }}
                                    onClick={() => mainImage && setIsLightboxOpen(true)}
                                >
                                    {mainImage ? (
                                        <img src={`${FILE_URL}/${mainImage}`} alt={product.name} className="img-fluid rounded-3 product-zoom-image" style={{ maxHeight: '100%', objectFit: 'contain' }} />
                                    ) : (
                                        <div className="text-muted d-flex flex-column align-items-center">
                                            <FaBoxOpen size={64} className="mb-3 opacity-25" />
                                            <span>Görsel Yok</span>
                                        </div>
                                    )}
                                    {product.images && product.images.length > 1 && (
                                        <>
                                            <button className="slider-btn prev" onClick={handlePrevImage}><FaChevronLeft /></button>
                                            <button className="slider-btn next" onClick={handleNextImage}><FaChevronRight /></button>
                                        </>
                                    )}
                                </div>
                                
                                {product.images && product.images.length > 1 && (
                                    <div className="d-flex gap-3 overflow-auto px-2 py-2 w-100 justify-content-center">
                                        {product.images.map((img, idx) => (
                                            <div 
                                                key={idx}
                                                className={`thumbnail-item border rounded-3 p-1 bg-white ${mainImage === img.image_url ? 'border-primary shadow' : 'border-light'}`}
                                                style={{ cursor: 'pointer', width: '80px', height: '80px', flexShrink: 0, transition: 'all 0.2s' }}
                                                onClick={() => setMainImage(img.image_url)}
                                            >
                                                <img 
                                                    src={`${FILE_URL}/${img.image_url}`} 
                                                    alt={`thumb-${idx}`}
                                                    style={{ width: '100%', height: '100%', objectFit: 'cover', borderRadius: '6px' }}
                                                />
                                            </div>
                                        ))}
                                    </div>
                                )}
                            </div>
                        </div>

                        {/* Sağ Taraf: Ürün Bilgileri */}
                        <div className="col-lg-6 bg-white">
                            <div className="p-5 h-100 d-flex flex-column">
                                <div className="mb-2">
                                    {product.categories?.map(cat => (
                                        <span key={cat.id} className="badge bg-primary-soft text-primary me-2 px-3 py-2 rounded-pill fw-semibold">
                                            {i18n.language === 'fr' ? (cat.name_fr || cat.name) : cat.name}
                                        </span>
                                    ))}
                                </div>
                                
                                <h1 className="display-6 fw-bold text-dark mb-3">{product.name}</h1>
                                
                                {/* Rating Placeholder */}
                                <div className="d-flex align-items-center mb-4 text-warning small">
                                    <FaStar /> <FaStar /> <FaStar /> <FaStar /> <FaStar className="text-muted opacity-25" />
                                    <span className="text-muted ms-2 fw-medium">(4.0/5)</span>
                                </div>

                                {/* Fiyat ve Stok Kartı */}
                                <div className="price-container bg-light bg-opacity-50 border border-light rounded-4 p-4 mb-4 shadow-sm">
                                    <div className="d-flex justify-content-between align-items-start">
                                        <div>
                                            <div className="text-muted small text-uppercase fw-bold ls-1 mb-1">Satış Fiyatı</div>
                                            <div className="retail-price display-5 fw-bold text-dark">
                                                {selectedVariation ? Number(selectedVariation.retail_price).toLocaleString('tr-TR', { style: 'currency', currency: 'EUR' }) : '-'}
                                            </div>
                                            <div className="text-muted small mt-1">
                                                + KDV Dahil
                                            </div>
                                        </div>
                                        
                                        <div className="text-end">
                                            <button 
                                                className="btn btn-sm btn-link text-decoration-none text-muted d-flex align-items-center gap-2 p-0 mb-2" 
                                                onClick={() => setShowDealerPrice(!showDealerPrice)}
                                                title="Bayi Alış Fiyatını Göster/Gizle"
                                            >
                                                {showDealerPrice ? <FaEyeSlash size={18} /> : <FaEye size={18} />}
                                            </button>
                                            
                                            {showDealerPrice && selectedVariation && (
                                                <div className="dealer-price-box bg-white border border-danger text-danger px-3 py-2 rounded-3 shadow-sm animate__animated animate__fadeIn">
                                                    <small className="d-block text-muted" style={{fontSize: '0.7rem'}}>BAYİ ALIŞ</small>
                                                    Bayi Alış: {Number(selectedVariation.dealer_price).toLocaleString('tr-TR', { style: 'currency', currency: 'EUR' })}
                                                </div>
                                            )}
                                        </div>
                                    </div>

                                    <div className="mt-3 pt-3 border-top border-light d-flex align-items-center gap-3 text-muted small">
                                        {selectedVariation && selectedVariation.stock_quantity > 0 ? (
                                            <span className="text-success d-flex align-items-center gap-1 fw-bold bg-success-soft px-2 py-1 rounded">
                                                <FaCheckCircle /> Stokta Var ({selectedVariation.stock_quantity} {product.unit_type})
                                            </span>
                                        ) : (
                                            <span className="text-danger fw-bold bg-danger-soft px-2 py-1 rounded">Stokta Yok</span>
                                        )}
                                        <span className="d-flex align-items-center gap-1"><FaTruck /> Hızlı Kargo</span>
                                        <span className="d-flex align-items-center gap-1"><FaShieldAlt /> Garantili</span>
                                    </div>
                                </div>

                                {/* Varyasyon Seçimi */}
                                <div className="mb-4">
                                    <label className="form-label fw-bold text-dark mb-3 small text-uppercase ls-1">Seçenekler</label>
                                    <div className="variation-selector d-flex flex-wrap gap-2">
                                        {product.variations?.map(v => (
                                            <div 
                                                key={v.id} 
                                                className={`variation-box p-3 rounded-3 border ${selectedVariation?.id === v.id ? 'border-primary bg-primary-soft text-primary shadow-sm' : 'border-light bg-white text-muted'}`}
                                                style={{ cursor: 'pointer', minWidth: '100px', textAlign: 'center', transition: 'all 0.2s' }}
                                                onClick={() => {
                                                    setSelectedVariation(v);
                                                    if (v.image_url && !v.use_main_image) {
                                                        setMainImage(v.image_url);
                                                    }
                                                }}
                                            >
                                                <div className="fw-bold">{v.size}</div>
                                                <div className="small opacity-75">{v.color}</div>
                                            </div>
                                        ))}
                                    </div>
                                </div>

                                {/* Miktar ve Sepet */}
                                <div className="d-flex gap-3 align-items-stretch mb-5">
                                    <div className="form-floating" style={{ width: '120px' }}>
                                        <input 
                                            type="number" 
                                            className="form-control border-light bg-light fw-bold text-center" 
                                            id="quantityInput"
                                            min={product.min_order_limit}
                                            value={quantity}
                                            onChange={(e) => setQuantity(e.target.value)}
                                            placeholder="Miktar"
                                        />
                                        <label htmlFor="quantityInput">Miktar</label>
                                    </div>
                                    <button 
                                        className="btn btn-primary btn-lg flex-grow-1 shadow-sm d-flex align-items-center justify-content-center gap-2 rounded-3" 
                                        onClick={handleAddToCart}
                                        disabled={!selectedVariation || selectedVariation.stock_quantity <= 0}
                                    >
                                        <FaShoppingCart size={20} /> 
                                        <span>Sepete Ekle</span>
                                    </button>
                                    <button 
                                        className={`btn btn-lg ${isFavorite ? 'btn-danger' : 'btn-outline-secondary'} d-flex align-items-center justify-content-center px-3 rounded-3`}
                                        onClick={handleToggleFavorite}
                                        title={isFavorite ? "Favorilerden Çıkar" : "Favorilere Ekle"}
                                    >
                                        {isFavorite ? <FaHeart size={24} /> : <FaRegHeart size={24} />}
                                    </button>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>

                {/* Ürün Açıklaması - Tam Genişlik */}
                <div className="card border-0 shadow-lg rounded-4 overflow-hidden mb-5">
                    <div className="card-body p-5">
                        <h4 className="fw-bold mb-4 text-dark border-bottom pb-3">Ürün Açıklaması</h4>
                        <div className="text-muted product-description" style={{ lineHeight: '1.8', fontSize: '1rem' }} dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(product.description) }} />
                    </div>
                </div>

                {/* Benzer Ürünler */}
                {product.similarProducts && product.similarProducts.length > 0 && (
                    <div className="mt-5">
                        <div className="d-flex align-items-center justify-content-between mb-4">
                            <h3 className="fw-bold text-dark m-0">Benzer Ürünler</h3>
                            <Link to="/products" className="btn btn-outline-primary rounded-pill px-4">Tümünü Gör</Link>
                        </div>
                        <div className="row g-4">
                            {product.similarProducts.map(sim => (
                                <div key={sim.id} className="col-6 col-lg-3">
                                    <div className="card h-100 border-0 shadow-sm hover-shadow-lg transition-all rounded-4 overflow-hidden">
                                        <div className="position-relative bg-light" style={{ height: '220px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                                            {sim.main_image ? (
                                                <img src={`${FILE_URL}/${sim.main_image}`} alt={sim.name} style={{ maxHeight: '80%', maxWidth: '80%', objectFit: 'contain' }} />
                                            ) : (
                                                <span className="text-muted small">Resim Yok</span>
                                            )}
                                            <div className="position-absolute top-0 end-0 p-3">
                                                <span className="badge bg-white text-dark shadow-sm rounded-pill">Yeni</span>
                                            </div>
                                        </div>
                                        <div className="card-body p-4">
                                            <h6 className="card-title fw-bold text-truncate mb-2">{sim.name}</h6>
                                            <div className="d-flex justify-content-between align-items-center mt-3">
                                                <Link to={`/products/${sim.id}`} className="btn btn-sm btn-primary rounded-pill px-3">İncele</Link>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>
                )}
            </div>

            {/* Lightbox Modal (Tam Ekran Resim) */}
            {isLightboxOpen && (
                <div className="lightbox-overlay" onClick={() => setIsLightboxOpen(false)}>
                    <button className="lightbox-close-btn" onClick={() => setIsLightboxOpen(false)}>
                        <FaTimes size={30} />
                    </button>
                    <img src={`${FILE_URL}/${mainImage}`} alt={product.name} className="lightbox-image" onClick={(e) => e.stopPropagation()} />
                </div>
            )}
        </AdminLayout>
    );
};

export default ProductDetail;
